namespace Nbs.Xlr8PageTemplate.Utilities {
  export const imgPath = '~~image-library-path~~';

  export interface ElementBuilderAttributes {
    alt?: string;
    'aria-checked'?: boolean;
    'aria-controls'?: string;
    'aria-current'?: boolean;
    'aria-disabled'?: boolean;
    'aria-expanded'?: boolean;
    'aria-haspopup'?: boolean;
    'aria-hidden'?: boolean;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    'aria-level'?: '1' | '2' | '3' | '4' | '5' | '6' | 1 | 2 | 3 | 4 | 5 | 6;
    content?: string;
    'data-menu-item-id'?: string;
    href?: string;
    id?: string;
    name?: string;
    nonce?: string;
    placeholder?: string;
    rel?: string;
    role?: string;
    src?: string;
    target?: '_self' | '_blank';
    title?: string;
    type?: string;
  }

  export type ElementBuilderEventHandler<TEvent extends Event = Event> = (event: TEvent) => void;

  export interface ElementBuilderEventHandlers {
    click?: ElementBuilderEventHandler<MouseEvent>;
    input?: ElementBuilderEventHandler<InputEvent>;
  }

  export interface BaseElementBuilderParams {
    classes?: readonly string[];
    attributes?: ElementBuilderAttributes;
    children?: readonly (Node | string | undefined)[];
    eventHandlers?: ElementBuilderEventHandlers;
  }

  export interface ElementBuilderParams extends BaseElementBuilderParams {
    tag?: keyof HTMLElementTagNameMap | 'xpt-flyout-menu';
  }

  export interface SVGElementBuilderParams extends BaseElementBuilderParams {
    tag: keyof SVGElementTagNameMap;
  }

  export function createElement(params: ElementBuilderParams) {
    return applyElementArgs(document.createElement(params.tag ?? 'div'), params);
  }

  export function createSvgElement(params: SVGElementBuilderParams) {
    return applyElementArgs(document.createElementNS('http://www.w3.org/2000/svg', params.tag), params);
  }

  function applyElementArgs<T extends Element>(elem: T, params: BaseElementBuilderParams) {
    if (params.classes) {
      elem.classList.add(...params.classes.filter(x => !!x));
    }
    const attributesToAdd = params.attributes;
    if (attributesToAdd) {
      Object.keys(attributesToAdd).forEach(key => {
        const value = attributesToAdd[key as keyof ElementBuilderAttributes];
        if (value !== undefined && value !== null) {
          if (key === 'id') {
            elem.id = value as string;
          } else {
            elem.setAttribute(key, value.toString());
          }
        }
      });
    }
    if (params.children) {
      const children = params.children;
      children.forEach(child => {
        if (child !== undefined) {
          if (typeof child === 'string') {
            elem.appendChild(document.createTextNode(child));
          } else {
            elem.appendChild(child);
          }
        }
      });
    }
    const eventHandlers = params.eventHandlers;
    if (eventHandlers) {
      Object.keys(eventHandlers).forEach(eventName => {
        const eventHandler = eventHandlers[eventName as keyof ElementBuilderEventHandlers] as ElementBuilderEventHandler;

        elem.addEventListener(eventName, eventHandler);
      });
    }
    return elem;
  }

  export function getIconElement(iconName?: string, ariaLabel?: string, id?: string, additionalCssClass?: string) {
    if (!iconName) {
      return undefined;
    }

    if (iconName.endsWith('.svg')) {
      // ??? No idea why it's complaining about the escaped slash
      // eslint-disable-next-line no-useless-escape
      const noExtension = ariaLabel ? ariaLabel : iconName.replace(/\.[^\/.]+$/, '');
      return createSvgElement({
        tag: 'svg',
        classes: ['menu-icon'],
        attributes: {
          id,
          role: 'img',
          'aria-label': ariaLabel,
          'aria-hidden': !ariaLabel
        },
        children: [createSvgElement({
          tag: 'use',
          attributes: {
            href: `#xpt-${noExtension}`
          }
        })]
      });
    }
    return createElement({
      tag: 'i',
      classes: ['material-icons', additionalCssClass ?? ''],
      attributes: {
        id,
        role: 'img',
        'aria-label': ariaLabel,
        'aria-hidden': !ariaLabel
      },
      children: [iconName]
    });
  }

  export function initSvgContainer() {
    const container = getOrCreateElement('xpt-svg-container', 'body', 'beforeend', true);
    if (container && container.children.length === 0) {
      container.classList.add('xpt-display-none');
      const ajax = new XMLHttpRequest();
      ajax.open('GET', `${imgPath}/xpt-icons.svg`, true);
      ajax.send();
      ajax.onload = () => {
        if (container) {
          container.innerHTML = ajax.responseText;
        }
      }
    }
  }

  export function createLinkLabelElement(link: Navigation.Link | Navigation.Menu) {
    const badgeParentClasses = ['xpt-menu-link-label-badge'];
    const badgeClasses = ['xpt-menu-link-label-badge-value'];

    let badgeValue = '';
    let hideBadge = true;

    if (link.badge) {
        hideBadge = link.badge.hide === true;
        badgeValue = link.badge.value?.toString() ?? '';
    }

    if (hideBadge) {
      badgeParentClasses.push('xpt-menu-link-label-badge-hidden');
    }

    if (link.badge?.type == 'error') {
      badgeClasses.push('xpt-menu-link-label-badge-value-error');
    }

    return Utilities.createElement({
      classes: ['xpt-menu-link-label'],
      children: [
        Utilities.createElement({
          tag: 'span',
          children: [
            link.label,
            link.badge ? Utilities.createElement({
              tag: 'div',
              classes: badgeParentClasses,
              children: [
                Utilities.createElement({
                  tag: 'span',
                  classes: badgeClasses,
                  children: [badgeValue]
                }),
                link.badge?.label ? Utilities.createElement({
                  tag: 'span',
                  classes: ['xpt-menu-link-label-badge-value-description'],
                  children: [link.badge.label]
                }) : undefined
              ]
            }) : undefined
          ]
        })
      ]
    });
  }

}
